import { useEffect, useRef } from 'react';
import { useAppSelector } from '../store/hooks';
import { showNotification } from './notificationUtils';
import { getTodayString, isHabitActiveForDate, getDayOfWeek } from './dateUtils';
import { useTranslation } from 'react-i18next';

export const useDailyProgressNotifications = () => {
  const todos = useAppSelector((state) => state.todos.items);
  const habits = useAppSelector((state) => state.habits.items);
  const habitCompletions = useAppSelector((state) => state.habits.completions);
  const trackers = useAppSelector((state) => state.trackers.items);
  const trackerEntries = useAppSelector((state) => state.trackers.entries);
  const currentUser = useAppSelector((state) => state.user.profile);
  const { t } = useTranslation();

  const lastNotificationDateRef = useRef<string | null>(null);

  useEffect(() => {
    // Only run for authenticated users
    if (!currentUser) return;

    const checkDailyProgress = () => {
      const now = new Date();
      const todayString = getTodayString();

      // Only show notification at 8 PM
      if (now.getHours() !== 20 || now.getMinutes() !== 0) {
        return;
      }

      // Don't show notification if already shown today
      if (lastNotificationDateRef.current === todayString) {
        return;
      }

      // Count incomplete todos
      const incompleteTodos = todos.filter(todo => !todo.completed).length;

      // Count incomplete habits for today
      const today = new Date();
      const todayDayOfWeek = getDayOfWeek(today);
      const incompleteHabits = habits.filter(habit => {
        // Check if habit is active today
        if (!isHabitActiveForDate(habit.activeForDays, today)) {
          return false;
        }
        // Check if there's a completion for today
        const todayCompletion = habitCompletions.find(
          c => c.habitId === habit.id && c.date === todayString
        );
        return !todayCompletion || !todayCompletion.completed;
      }).length;

      // Count incomplete trackers for today
      const incompleteTrackers = trackers.filter(tracker => {
        // Check if tracker is active today
        if (tracker.trackingDays && tracker.trackingDays.length > 0) {
          if (!tracker.trackingDays.includes(todayDayOfWeek)) {
            return false;
          }
        }
        // Check if there's an entry for today
        const todayEntry = trackerEntries.find(
          e => e.trackerId === tracker.id && e.date === todayString
        );
        return !todayEntry;
      }).length;

      // Only show notification if there are incomplete items
      const totalIncomplete = incompleteTodos + incompleteHabits + incompleteTrackers;
      if (totalIncomplete > 0) {
        const parts = [];
        if (incompleteTodos > 0) parts.push(`${incompleteTodos} ${t('notifications.wordTodos')}`);
        if (incompleteHabits > 0) parts.push(`${incompleteHabits} ${t('notifications.wordHabits')}`);
        if (incompleteTrackers > 0) parts.push(`${incompleteTrackers} ${t('notifications.wordTrackers')}`);

        let items = '';
        if (parts.length === 1) {
          items = parts[0];
        } else if (parts.length === 2) {
          items = parts.join(` ${t('notifications.wordAnd')} `);
        } else if (parts.length === 3) {
          items = `${parts[0]}, ${parts[1]} ${t('notifications.wordAnd')} ${parts[2]}`;
        }

        const message = `${t('notifications.remainingIntro')} ${items} ${t('notifications.remainingOutro')}`;

        showNotification(t('notifications.dailyProgressTitle'), {
          body: message,
          tag: 'daily-progress',
          requireInteraction: false,
        });

        lastNotificationDateRef.current = todayString;
      }
    };

    // Check immediately on mount
    checkDailyProgress();

    // Set up interval to check every minute
    const interval = setInterval(checkDailyProgress, 60 * 1000);

    return () => clearInterval(interval);
  }, [todos, habits, habitCompletions, trackers, trackerEntries, currentUser, t]);
};
